import { renderTemplateContent } from "../contentRenderer";
import { mergeTemplateDesign, templateDesignToCssVars } from "../design";
import { DefaultTemplateLink } from "../templateLink";
import type {
    ResolvedTemplateDesign,
    SiteTemplateDefinition,
    TemplateLinkComponent,
    TemplateRenderProps,
} from "../types";
import { WebBuilderPageRegion } from "../WebBuilderPageRegion";
import { WebGlobalBuilderRegion } from "../WebGlobalBuilderRegion";
import { webSystemTemplateDesignDefaults } from "../webSystemTemplateDesign";
import { VoltFlowMobileMenu } from "./VoltFlowMobileMenu";

function VoltFlowStyles() {
    return (
        <style>{`
            [data-web-template="volt-flow"] {
                background: var(--site-background);
                color: var(--site-text);
                isolation: isolate;
                scroll-behavior: smooth;
                scroll-padding-top: 76px;
            }

            [data-web-template="volt-flow"] *,
            [data-web-template="volt-flow"] *::before,
            [data-web-template="volt-flow"] *::after {
                box-sizing: border-box;
            }

            [data-web-template="volt-flow"] ::selection {
                background: var(--site-accent);
                color: var(--site-primary);
            }

            [data-web-template="volt-flow"] .site-builder-section {
                overflow: hidden;
                padding-inline: clamp(1.25rem, 2.5vw, 2.5rem);
                position: relative;
            }

            [data-web-template="volt-flow"] .site-builder-grid {
                display: grid;
                gap: clamp(1.15rem, 2.8vw, 2.75rem);
                margin-inline: auto;
                width: min(100%, var(--site-container));
            }

            [data-web-template="volt-flow"] .site-builder-grid:not(.site-builder-grid-placed) {
                grid-template-columns: repeat(12, minmax(0, 1fr));
            }

            [data-web-template="volt-flow"] .site-builder-grid:not(.site-builder-grid-placed) > .site-builder-grid-item {
                grid-column: 1 / -1;
            }

            [data-web-template="volt-flow"] .site-builder-column {
                align-content: start;
                display: grid;
                gap: 1rem;
                min-width: 0;
            }

            [data-web-template="volt-flow"] .site-builder-column-inline {
                align-items: center;
                display: flex;
                flex-wrap: wrap;
            }

            [data-web-template="volt-flow"] .site-builder-text-h1,
            [data-web-template="volt-flow"] .site-builder-text-h2,
            [data-web-template="volt-flow"] .site-builder-text-h3 {
                text-wrap: balance;
            }

            [data-web-template="volt-flow"] .site-builder-text-h1 {
                line-height: .84 !important;
            }

            [data-web-template="volt-flow"] .site-builder-button {
                letter-spacing: .09em;
                min-height: 52px;
                text-transform: uppercase;
                transition: background-color 180ms ease, border-color 180ms ease, color 180ms ease, transform 180ms ease;
            }

            [data-web-template="volt-flow"] .site-builder-button:hover {
                transform: translateX(4px);
            }

            [data-web-template="volt-flow"] .site-builder-card {
                border-radius: 0 !important;
                transition: border-color 200ms ease, box-shadow 200ms ease, transform 200ms ease;
            }

            [data-web-template="volt-flow"] .site-builder-card:hover {
                border-color: color-mix(in srgb, var(--site-accent) 50%, var(--site-border)) !important;
                box-shadow: 0 20px 50px var(--site-shadow-color);
                transform: translateY(-3px);
            }

            [data-web-template="volt-flow"] .site-builder-navigation {
                align-items: center;
                display: flex;
                flex-wrap: wrap;
                gap: clamp(.9rem, 2vw, 1.8rem);
                justify-content: center;
            }

            [data-web-template="volt-flow"] .site-builder-navigation-item,
            [data-web-template="volt-flow"] .site-builder-navigation-link {
                display: inline-flex;
            }

            [data-web-template="volt-flow"] [data-web-region="header"] {
                backdrop-filter: blur(18px);
                box-shadow: none;
                position: sticky;
                top: 0;
                transition: box-shadow 220ms ease, background-color 220ms ease;
                z-index: 60;
            }

            [data-web-template="volt-flow"] [data-web-region="header"][data-volt-flow-header-scrolled] {
                box-shadow: 0 12px 34px color-mix(in srgb, var(--site-shadow-color) 88%, transparent);
            }

            [data-web-template="volt-flow"] [data-web-region="header"] > .site-builder-grid {
                align-items: center;
            }

            [data-web-template="volt-flow"] [data-document-role="headerNavigation"] .site-builder-text {
                display: inline-flex;
                position: relative;
                width: auto !important;
            }

            [data-web-template="volt-flow"] [data-document-role="headerNavigation"] .site-builder-text::after {
                background: var(--site-accent);
                bottom: -.5rem;
                content: "";
                height: 2px;
                left: 0;
                position: absolute;
                transform: scaleX(0);
                transform-origin: left;
                transition: transform 160ms ease;
                width: 100%;
            }

            [data-web-template="volt-flow"] [data-document-role="headerNavigation"] .site-builder-text:hover::after {
                transform: scaleX(1);
            }

            [data-web-template="volt-flow"] [data-document-role="headerActions"] {
                flex-wrap: nowrap;
                justify-content: flex-end;
            }

            [data-web-template="volt-flow"] [data-document-role="brand"] {
                border-left: 4px solid var(--site-accent);
                padding-left: clamp(.75rem, 1.3vw, 1.25rem);
            }

            [data-web-template="volt-flow"] [data-document-role="headerCta"] {
                clip-path: polygon(0 0, calc(100% - 12px) 0, 100% 50%, calc(100% - 12px) 100%, 0 100%);
                padding-right: 1.75rem !important;
            }

            [data-web-template="volt-flow"] [data-document-role="headerCta"],
            [data-web-template="volt-flow"] [data-document-role="localeActions"] {
                flex: 0 0 auto;
            }

            [data-web-template="volt-flow"] [data-document-role="heroCopy"] {
                column-gap: .75rem;
                display: grid;
                grid-template-columns: repeat(12, minmax(0, 1fr));
                row-gap: 1.25rem;
            }

            [data-web-template="volt-flow"] [data-document-role="heroCopy"] > :not([data-document-role="heroPrimaryAction"], [data-document-role="heroSecondaryAction"]) {
                grid-column: 1 / -1;
            }

            [data-web-template="volt-flow"] [data-document-role="heroCopy"] > [data-document-role="heroPrimaryAction"] {
                grid-column: 1 / span 6;
            }

            [data-web-template="volt-flow"] [data-document-role="heroCopy"] > [data-document-role="heroSecondaryAction"] {
                grid-column: 7 / span 6;
            }

            [data-web-template="volt-flow"] [data-document-role="heroVisual"],
            [data-web-template="volt-flow"] [data-document-role="editorialVisual"] {
                position: relative;
            }

            [data-web-template="volt-flow"] [data-document-role="heroVisual"] .site-builder-image,
            [data-web-template="volt-flow"] [data-document-role="editorialVisual"] .site-builder-image {
                min-height: 100%;
            }

            [data-web-template="volt-flow"] [data-document-role="heroFloatingProof"] {
                bottom: 1.5rem;
                left: 1.5rem;
                position: absolute;
                z-index: 2;
            }

            [data-web-template="volt-flow"] [data-web-region="homeHeroIntro"] {
                padding-inline: 0;
            }

            [data-web-template="volt-flow"] [data-web-region="homeHeroIntro"] > .site-builder-grid {
                gap: 0 !important;
                max-width: none;
                width: 100%;
            }

            [data-web-template="volt-flow"] [data-web-region="homeHeroIntro"] [data-document-role="heroCopy"] {
                padding-left: max(1.25rem, calc((100cqw - var(--site-container)) / 2));
                padding-right: clamp(1.5rem, 4vw, 5rem);
            }

            [data-web-template="volt-flow"] [data-document-role="heroVisual"] {
                overflow: hidden;
            }

            [data-web-template="volt-flow"] [data-document-role="heroVisual"]::after,
            [data-web-template="volt-flow"] [data-document-role="editorialVisual"]::after {
                background: linear-gradient(118deg, color-mix(in srgb, var(--site-primary) 30%, transparent), transparent 46%);
                content: "";
                inset: 0;
                pointer-events: none;
                position: absolute;
                z-index: 1;
            }

            [data-web-template="volt-flow"] [data-document-role="heroVisual"] img,
            [data-web-template="volt-flow"] [data-document-role="editorialVisual"] img,
            [data-web-template="volt-flow"] [data-web-region*="Gallery"] img {
                filter: saturate(.72) contrast(1.08);
                transition: filter 500ms ease, transform 650ms cubic-bezier(.2,.8,.2,1);
            }

            [data-web-template="volt-flow"] [data-document-role="heroVisual"]:hover img,
            [data-web-template="volt-flow"] [data-document-role="editorialVisual"]:hover img,
            [data-web-template="volt-flow"] [data-web-region*="Gallery"] .site-builder-card:hover img {
                filter: saturate(1) contrast(1.04);
                transform: scale(1.035);
            }

            [data-web-template="volt-flow"] [data-web-region="homeContactEmergency"],
            [data-web-template="volt-flow"] [data-web-region="contactEmergency"],
            [data-web-template="volt-flow"] [data-web-region="electricalContact"],
            [data-web-template="volt-flow"] [data-web-region="plumbingContact"] {
                border-block: 1px solid color-mix(in srgb, var(--site-accent) 40%, transparent);
            }

            [data-web-template="volt-flow"] [data-web-region="homeTrust"] .site-builder-card,
            [data-web-template="volt-flow"] [data-web-region="proofMetrics"] .site-builder-card {
                background: transparent !important;
                border-width: 0 !important;
                box-shadow: none !important;
                padding-block: .75rem !important;
            }

            [data-web-template="volt-flow"] [data-web-region="homeProcess"] .site-builder-card,
            [data-web-template="volt-flow"] [data-web-region="workflowSteps"] .site-builder-card,
            [data-web-template="volt-flow"] [data-web-region="aboutPrinciples"] .site-builder-card {
                border-top: 4px solid var(--site-primary) !important;
            }

            [data-web-template="volt-flow"] [data-web-region="footer"] {
                border-top: 4px solid var(--site-accent);
                position: relative;
            }

            [data-web-template="volt-flow"] [data-volt-flow-reveal="true"] {
                opacity: 0;
                transform: translateY(26px);
                transition: opacity 700ms cubic-bezier(.22,1,.36,1), transform 700ms cubic-bezier(.22,1,.36,1);
            }

            [data-web-template="volt-flow"] [data-volt-flow-reveal="true"][data-volt-flow-revealed="true"] {
                opacity: 1;
                transform: translateY(0);
            }

            [data-web-template="volt-flow"][data-web-template-editor="true"] [data-volt-flow-reveal="true"] {
                opacity: 1;
                transform: none;
            }

            [data-web-template="volt-flow"] [data-web-region="homeTrust"] > .site-builder-grid > .site-builder-grid-item:not(:last-child) {
                border-right: 1px solid var(--site-border);
                padding-right: clamp(.75rem, 2vw, 1.75rem);
            }

            [data-web-template="volt-flow"] .volt-flow-mobile-menu-toggle {
                display: none;
            }

            @container (max-width: 900px) {
                [data-web-template="volt-flow"] .site-builder-grid[data-site-builder-stack-on-mobile="true"] {
                    grid-auto-rows: auto !important;
                    grid-template-columns: minmax(0, 1fr) !important;
                }

                [data-web-template="volt-flow"] .site-builder-grid[data-site-builder-stack-on-mobile="true"] > .site-builder-grid-item {
                    grid-column: 1 !important;
                    grid-row: auto !important;
                }

                [data-web-template="volt-flow"] [data-web-region="homeTrust"] > .site-builder-grid {
                    grid-template-columns: repeat(2, minmax(0, 1fr)) !important;
                }

                [data-web-template="volt-flow"] [data-web-region="homeTrust"] > .site-builder-grid > .site-builder-grid-item {
                    border-right: 0;
                    grid-column: auto !important;
                    grid-row: auto !important;
                }

                [data-web-template="volt-flow"] [data-document-role="heroCopy"] {
                    grid-template-columns: minmax(0, 1fr);
                }

                [data-web-template="volt-flow"] [data-document-role="heroCopy"] > * {
                    grid-column: 1 !important;
                }

                [data-web-template="volt-flow"] [data-document-role="heroVisual"] {
                    min-height: clamp(420px, 68vw, 600px);
                }

                [data-web-template="volt-flow"] [data-web-region="header"] {
                    background: var(--site-primary) !important;
                    min-height: 72px;
                    padding-inline: 1rem;
                }

                [data-web-template="volt-flow"] [data-web-region="header"] > .site-builder-grid {
                    grid-template-columns: minmax(0, 1fr) auto !important;
                }

                [data-web-template="volt-flow"] [data-web-region="header"] > .site-builder-grid > .site-builder-grid-item:has(> [data-document-role="brand"]) {
                    display: block;
                    grid-column: 1 !important;
                    grid-row: 1 !important;
                }

                [data-web-template="volt-flow"] [data-web-region="header"] > .site-builder-grid > .site-builder-grid-item:has(> [data-document-role="headerNavigation"]),
                [data-web-template="volt-flow"] [data-web-region="header"] > .site-builder-grid > .site-builder-grid-item:has(> [data-document-role="headerActions"]) {
                    display: none;
                }

                [data-web-template="volt-flow"] [data-web-region="header"] .volt-flow-mobile-menu-toggle {
                    align-items: center;
                    background: transparent;
                    border: 0;
                    cursor: pointer;
                    display: grid;
                    gap: 7px;
                    height: 44px;
                    justify-self: end;
                    padding: 10px;
                    place-content: center;
                    position: absolute;
                    right: 1rem;
                    top: 14px;
                    width: 44px;
                    z-index: 3;
                }

                [data-web-template="volt-flow"] .volt-flow-mobile-menu-toggle > span {
                    background: var(--site-accent);
                    display: block;
                    height: 2px;
                    transition: transform 160ms ease;
                    width: 26px;
                }

                [data-web-template="volt-flow"] [data-web-region="header"][data-volt-flow-mobile-menu-open] {
                    background: var(--site-primary) !important;
                    bottom: 0;
                    left: 0;
                    overflow-y: auto;
                    position: fixed;
                    right: 0;
                    top: 0;
                }

                [data-web-template="volt-flow"] [data-web-region="header"][data-volt-flow-mobile-menu-open] > .site-builder-grid {
                    align-content: start;
                    min-height: 100%;
                    padding-bottom: 2rem;
                }

                [data-web-template="volt-flow"] [data-web-region="header"][data-volt-flow-mobile-menu-open] > .site-builder-grid > .site-builder-grid-item:has(> [data-document-role="headerNavigation"]),
                [data-web-template="volt-flow"] [data-web-region="header"][data-volt-flow-mobile-menu-open] > .site-builder-grid > .site-builder-grid-item:has(> [data-document-role="headerActions"]) {
                    display: block;
                    grid-column: 1 / -1 !important;
                }

                [data-web-template="volt-flow"] [data-web-region="header"][data-volt-flow-mobile-menu-open] > .site-builder-grid > .site-builder-grid-item:has(> [data-document-role="headerNavigation"]) {
                    grid-row: 2 !important;
                    margin-top: clamp(3rem, 10vh, 6rem);
                }

                [data-web-template="volt-flow"] [data-web-region="header"][data-volt-flow-mobile-menu-open] > .site-builder-grid > .site-builder-grid-item:has(> [data-document-role="headerActions"]) {
                    grid-row: 3 !important;
                }

                [data-web-template="volt-flow"] [data-web-region="header"][data-volt-flow-mobile-menu-open] [data-document-role="headerNavigation"],
                [data-web-template="volt-flow"] [data-web-region="header"][data-volt-flow-mobile-menu-open] [data-document-role="headerActions"] {
                    align-items: stretch;
                    flex-direction: column;
                    gap: 0 !important;
                }

                [data-web-template="volt-flow"] [data-web-region="header"][data-volt-flow-mobile-menu-open] [data-document-role="headerNavigation"] > [data-site-builder-block="text"] {
                    border-bottom: 1px solid var(--site-border);
                    padding-block: 1rem;
                    width: 100%;
                }

                [data-web-template="volt-flow"] [data-web-region="header"][data-volt-flow-mobile-menu-open] [data-document-role="headerNavigation"] .site-builder-text {
                    display: block;
                    font-family: var(--site-heading-font) !important;
                    font-size: clamp(1.8rem, 8vw, 2.8rem) !important;
                    font-weight: 760 !important;
                    width: 100% !important;
                }

                [data-web-template="volt-flow"] [data-web-region="header"][data-volt-flow-mobile-menu-open] .web-site-navigation {
                    width: 100%;
                }

                [data-web-template="volt-flow"] [data-web-region="header"][data-volt-flow-mobile-menu-open] .site-navigation-mobile-tree > .site-navigation-mobile-item {
                    border-color: color-mix(in srgb, var(--site-accent) 34%, transparent);
                }

                [data-web-template="volt-flow"] [data-web-region="header"][data-volt-flow-mobile-menu-open] [data-document-role="headerActions"] {
                    gap: .75rem !important;
                    margin-top: 1rem;
                }

                [data-web-template="volt-flow"] [data-web-region="header"][data-volt-flow-mobile-menu-open] [data-document-role="headerActions"] > *,
                [data-web-template="volt-flow"] [data-web-region="header"][data-volt-flow-mobile-menu-open] [data-document-role="headerCta"] {
                    width: 100% !important;
                }

                [data-web-template="volt-flow"] [data-web-region="header"][data-volt-flow-mobile-menu-open] .volt-flow-mobile-menu-toggle > span:first-child {
                    transform: translateY(9px) rotate(45deg);
                }

                [data-web-template="volt-flow"] [data-web-region="header"][data-volt-flow-mobile-menu-open] .volt-flow-mobile-menu-toggle > span:nth-child(2) {
                    opacity: 0;
                }

                [data-web-template="volt-flow"] [data-web-region="header"][data-volt-flow-mobile-menu-open] .volt-flow-mobile-menu-toggle > span:last-child {
                    transform: translateY(-9px) rotate(-45deg);
                }
            }

            @container (max-width: 640px) {
                [data-web-template="volt-flow"] .site-builder-section {
                    padding-inline: 1rem;
                }

                [data-web-template="volt-flow"] [data-document-role="heroPrimaryAction"],
                [data-web-template="volt-flow"] [data-document-role="heroSecondaryAction"] {
                    width: 100% !important;
                }

                [data-web-template="volt-flow"] [data-document-role="heroFloatingProof"] {
                    bottom: 1rem;
                    left: 1rem;
                }
            }

            @media (prefers-reduced-motion: reduce) {
                [data-web-template="volt-flow"] {
                    scroll-behavior: auto;
                }

                [data-web-template="volt-flow"] *,
                [data-web-template="volt-flow"] *::before,
                [data-web-template="volt-flow"] *::after {
                    animation-duration: .01ms !important;
                    animation-iteration-count: 1 !important;
                    scroll-behavior: auto !important;
                    transition-duration: .01ms !important;
                }

                [data-web-template="volt-flow"] [data-volt-flow-reveal="true"] {
                    opacity: 1;
                    transform: none;
                }
            }
        `}</style>
    );
}

function voltFlowBrandCssVars(design: ResolvedTemplateDesign): Record<string, string> {
    const usesDefaultBrandColors = design.colors.primary.toLowerCase() === "#081a2f"
        && design.colors.accent.toLowerCase() === "#c9ff45";

    return {
        "--volt-flow-action": usesDefaultBrandColors ? "#1559ff" : design.colors.primary,
        "--volt-flow-highlight": usesDefaultBrandColors ? "#65e8ff" : design.colors.accent,
    };
}

function VoltFlowFallbackPage({
    props,
    design,
    LinkComponent,
}: {
    props: TemplateRenderProps;
    design: ResolvedTemplateDesign;
    LinkComponent: TemplateLinkComponent;
}) {
    const page = props.page;

    if (!page) {
        return null;
    }

    return (
        <main className="px-5 py-20 sm:px-6" data-builder-document-region="page">
            <article className="mx-auto max-w-[900px] leading-8 text-[var(--site-muted)] [&_a]:text-[var(--site-primary)] [&_blockquote]:border-l-4 [&_blockquote]:border-[var(--site-accent)] [&_blockquote]:pl-5 [&_h1]:mb-6 [&_h1]:font-[var(--site-heading-font)] [&_h1]:text-5xl [&_h1]:font-bold [&_h1]:leading-none [&_h1]:text-[var(--site-text)] [&_h2]:mb-3 [&_h2]:mt-9 [&_h2]:font-[var(--site-heading-font)] [&_h2]:text-3xl [&_h2]:font-bold [&_h2]:text-[var(--site-text)] [&_li]:mb-2 [&_ol]:mb-6 [&_ol]:list-decimal [&_ol]:pl-6 [&_p]:mb-5 [&_strong]:text-[var(--site-text)] [&_ul]:mb-6 [&_ul]:list-disc [&_ul]:pl-6">
                <h1>{page.title}</h1>
                {renderTemplateContent(
                    page,
                    design,
                    LinkComponent,
                    props.leadForm,
                    props.readOnly,
                )}
            </article>
        </main>
    );
}

export const voltFlowTemplate: SiteTemplateDefinition = {
    manifest: {
        code: "volt-flow",
        name: "Elektrikář a instalatér",
        type: "website",
        category: "trades",
        description: "Výrazná technická šablona pro elektrikáře, instalatéry a společné servisní týmy s havarijní linkou, realizacemi a poptávkami.",
        previewImage: "/templates/volt-flow/preview.jpg",
        defaults: webSystemTemplateDesignDefaults["volt-flow"],
        pageTypes: {
            home: { label: "Volt / Flow homepage", slots: ["richContent"] },
            content: { label: "Volt / Flow content page", slots: ["richContent"] },
            contact: { label: "Volt / Flow contact page", slots: ["richContent"] },
        },
        editable: {
            colors: true,
            typography: true,
            radius: true,
            header: ["builder"],
            footer: ["builder"],
            buttons: ["solid", "outline"],
        },
    },
    render: (props: TemplateRenderProps) => {
        const design = mergeTemplateDesign(
            voltFlowTemplate.manifest.defaults,
            props.designOverride ?? props.site?.templateDesign,
        );
        const LinkComponent = props.LinkComponent ?? DefaultTemplateLink;

        return (
            <div
                className="min-h-screen overflow-x-hidden bg-[var(--site-background)] font-[var(--site-body-font)] text-[var(--site-text)] antialiased"
                data-web-template="volt-flow"
                data-web-template-editor={props.editorMode ? "true" : undefined}
                style={{
                    ...templateDesignToCssVars(design),
                    ...voltFlowBrandCssVars(design),
                    containerType: "inline-size",
                }}
            >
                <VoltFlowStyles />
                <WebGlobalBuilderRegion
                    design={design}
                    LinkComponent={LinkComponent}
                    props={props}
                    region="header"
                >
                    <VoltFlowMobileMenu
                        languageCode={props.page?.languageCode ?? props.site?.defaultLanguageCode}
                    />
                </WebGlobalBuilderRegion>
                <WebBuilderPageRegion
                    design={design}
                    fallback={(
                        <VoltFlowFallbackPage
                            design={design}
                            LinkComponent={LinkComponent}
                            props={props}
                        />
                    )}
                    LinkComponent={LinkComponent}
                    props={props}
                />
                <WebGlobalBuilderRegion
                    design={design}
                    LinkComponent={LinkComponent}
                    props={props}
                    region="footer"
                />
            </div>
        );
    },
};
